/**
 * Workflow Showcase Component
 * 
 * A showcase page displaying all 15 enhanced workflow types
 * with interactive mascot guide
 */

import {
    Badge,
    Card,
    Container,
    Grid,
    Group,
    Modal,
    Paper,
    ScrollArea,
    Text,
    Title,
    Tooltip
} from '@mantine/core';
import '@mantine/core/styles.css';
import React, { useEffect, useRef, useState } from 'react';
import {
    ActorDescriptorEnhanced,
    ContextEnhanced,
    EscalationEnhanced,
    ExecutionResultEnhanced,
    InstanceEnhanced,
    IntegrationEnhanced,
    JobStatusEnhanced,
    PolicyEnhanced,
    ResourceEnhanced,
    ScheduleEnhanced,
    SignalEnhanced,
    TaskEnhanced,
    TimelineItemEnhanced,
    TransitionCatalogEnhanced,
    WorkflowDefinitionEnhanced,
    WorkflowMetadata
} from './index';
import { getMascot } from './mascot';

interface WorkflowShowcaseProps {
    showMascot?: boolean;
    mascotName?: string; // optional registry key to pick a mascot
}

export const WorkflowShowcase: React.FC<WorkflowShowcaseProps> = ({ showMascot = true, mascotName }: WorkflowShowcaseProps) => {
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [modalOpened, setModalOpened] = useState(false);
    const mascotRef = useRef<HTMLDivElement>(null);
    const [mascotPosition, setMascotPosition] = useState({ x: 50, y: 100 });
    const [mascotMessage, setMascotMessage] = useState('Nyob zoo! 👋 Click on any workflow type to explore! (Welcome in Hmong)');

    const workflowTypes = [
        { id: 'WorkflowDefinition', Component: WorkflowDefinitionEnhanced },
        { id: 'Instance', Component: InstanceEnhanced },
        { id: 'Signal', Component: SignalEnhanced },
        { id: 'ExecutionResult', Component: ExecutionResultEnhanced },
        { id: 'TimelineItem', Component: TimelineItemEnhanced },
        { id: 'Task', Component: TaskEnhanced },
        { id: 'JobStatus', Component: JobStatusEnhanced },
        { id: 'ActorDescriptor', Component: ActorDescriptorEnhanced },
        { id: 'TransitionCatalog', Component: TransitionCatalogEnhanced },
        { id: 'Policy', Component: PolicyEnhanced },
        { id: 'Context', Component: ContextEnhanced },
        { id: 'Schedule', Component: ScheduleEnhanced },
        { id: 'Resource', Component: ResourceEnhanced },
        { id: 'Integration', Component: IntegrationEnhanced },
        { id: 'Escalation', Component: EscalationEnhanced }
    ];

    // Mascot animation
    useEffect(() => {
        if (showMascot && mascotRef.current) {
            const animateMascot = () => {
                const randomX = Math.random() * (window.innerWidth - 100);
                const randomY = Math.random() * 200 + 50;
                setMascotPosition({ x: randomX, y: randomY });
            };

            const interval = setInterval(animateMascot, 5000);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [showMascot]);

    const handleCardClick = (typeId: string, event: React.MouseEvent<HTMLDivElement>) => {
        setSelectedType(typeId);
        setModalOpened(true);

        if (showMascot) {
            const rect = event.currentTarget.getBoundingClientRect();
            setMascotPosition({
                x: rect.left + rect.width / 2 - 40,
                y: rect.top - 80
            });

            const metadata = WorkflowMetadata[typeId as keyof typeof WorkflowMetadata];
            setMascotMessage(`${metadata.title}: ${metadata.description}`);
        }
    };

    const renderSelectedComponent = () => {
        if (!selectedType) return null;
        const workflow = workflowTypes.find(w => w.id === selectedType);
        if (!workflow) return null;

        const Component = workflow.Component;
        return <Component />;
    };

    return (
        <Container size="xl" style={{ position: 'relative' }}>
            {/* Mascot */}
            {showMascot && (
                <div
                    ref={mascotRef}
                    style={{
                        position: 'fixed',
                        left: mascotPosition.x,
                        top: mascotPosition.y,
                        transition: 'all 1s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                        zIndex: 1000,
                        pointerEvents: 'none'
                    }}
                >
                    <div style={{
                        background: 'linear-gradient(135deg, #e74c3c 0%, #3498db 50%, #2ecc71 100%)',
                        borderRadius: '50%',
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                        position: 'relative',
                        animation: 'float 3s ease-in-out infinite',
                        border: '3px solid #fff',
                        overflow: 'hidden'
                    }}>
                        {(() => {
                            const def = getMascot(mascotName);
                            const Cmp = def.component;
                            return <Cmp size={def.defaultSize ?? 60} />;
                        })()}

                        {/* Speech bubble */}
                        <div style={{
                            position: 'absolute',
                            bottom: -60,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'white',
                            padding: '8px 12px',
                            borderRadius: 8,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            minWidth: 200,
                            maxWidth: 300,
                            pointerEvents: 'auto'
                        }}>
                            <Text size="sm">{mascotMessage}</Text>
                            <div style={{
                                position: 'absolute',
                                top: -8,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderBottom: '8px solid white'
                            }} />
                        </div>
                    </div>
                </div>
            )}

            <Paper shadow="sm" p="lg">
                <Group justify="space-between" mb="lg">
                    <div>
                        <Title order={1}>
                            <Text
                                inherit
                                component="span"
                                variant="gradient"
                                gradient={{ from: 'blue', to: 'purple', deg: 45 }}
                            >
                                Workflow Types Showcase
                            </Text>
                        </Title>
                        <Text color="dimmed">Explore all 15 enhanced workflow types with Mantine UI & Tabulator</Text>
                    </div>
                    <Badge size="lg" variant="gradient" gradient={{ from: 'orange', to: 'red', deg: 45 }}>
                        16 Types Available
                    </Badge>
                </Group>

                <ScrollArea>
                    <Grid>
                        {workflowTypes.map((workflow) => {
                            const metadata = WorkflowMetadata[workflow.id as keyof typeof WorkflowMetadata];
                            if (!metadata) return null;

                            return (
                                <Grid.Col key={workflow.id} span={4}>
                                    <Card
                                        withBorder
                                        shadow="sm"
                                        radius="md"
                                        style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                                        onClick={(e: React.MouseEvent<HTMLDivElement>) => handleCardClick(workflow.id, e)}
                                        onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '';
                                        }}
                                    >
                                        <Group justify="space-between" mb="xs">
                                            <Text size="xl">{metadata.icon}</Text>
                                            <Badge color={metadata.color as 'blue' | 'red' | 'green' | 'orange' | 'grape' | 'teal' | 'violet' | 'cyan' | 'pink' | 'yellow' | 'lime' | 'indigo' | 'gray'} variant="light">
                                                {workflow.id.replace(/([A-Z])/g, ' $1').trim()}
                                            </Badge>
                                        </Group>

                                        <Title order={5} mb="xs">{metadata.title}</Title>
                                        <Text size="sm" color="dimmed" lineClamp={2}>
                                            {metadata.description}
                                        </Text>

                                        <Group gap={4} mt="xs">
                                            {metadata.features.slice(0, 3).map((feature, idx) => (
                                                <Badge key={idx} size="xs" variant="dot">
                                                    {feature}
                                                </Badge>
                                            ))}
                                            {metadata.features.length > 3 && (
                                                <Tooltip label={metadata.features.slice(3).join(', ')}>
                                                    <Badge size="xs" variant="filled">
                                                        +{metadata.features.length - 3}
                                                    </Badge>
                                                </Tooltip>
                                            )}
                                        </Group>
                                    </Card>
                                </Grid.Col>
                            );
                        })}
                    </Grid>
                </ScrollArea>

                <Group justify="center" mt="xl">
                    <Text size="sm" color="dimmed">
                        💡 Tip: Click on any card to see the full component in action
                    </Text>
                </Group>
            </Paper>

            {/* Modal for displaying selected component */}
            <Modal
                opened={modalOpened}
                onClose={() => {
                    setModalOpened(false);
                    setSelectedType(null);
                }}
                title={selectedType ? WorkflowMetadata[selectedType as keyof typeof WorkflowMetadata]?.title : ''}
                size="90%"
                fullScreen
            >
                <ScrollArea h="calc(100vh - 100px)">
                    {renderSelectedComponent()}
                </ScrollArea>
            </Modal>

            <style>{`
                @keyframes float {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }
            `}</style>
        </Container>
    );
};

export default WorkflowShowcase;
